// packages/shared/src/types/addon-equipment.ts

import { z } from 'zod';

/**
 * Pricing models for add-on equipment
 */
export enum AddonPricingModel {
  HOURLY = 'HOURLY',
  HALF_HOURLY = 'HALF_HOURLY',
  FIXED = 'FIXED',
}

/**
 * Add-on equipment item with availability and pricing
 */
export interface AddonItem {
  id: string; // VenueInventory.id of the addon (venue-specific instance)
  addonItemId: string; // AddonItem catalog UUID (InventoryItem.id)
  name: string;
  description?: string;
  pricingModel: AddonPricingModel;
  pricePerUnit: number; // Rs. per hour/half-hour/fixed
  maxQuantity: number;
  availableQuantity: number;
  isRequired: boolean;
  displayOrder: number;
  /** Social proof caption, e.g. "72% of bookings include this". Null when attach rate < 30% or insufficient data. */
  recommendationText?: string | null;
}

/**
 * Selected addon in booking flow
 */
export interface SelectedAddon {
  addonItemId: string;
  venueInventoryId: string; // addon's venue inventory
  quantity: number;
}

/**
 * Zod schema for addon selection validation
 */
export const AddonSelectionSchema = z.object({
  addonItemId: z.string().uuid(),
  venueInventoryId: z.string().uuid(),
  quantity: z.number().int().min(1).max(10),
});

/**
 * Request to get available addons for a booking slot
 */
export interface AddonAvailabilityRequest {
  venueInventoryId: string; // primary inventory
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
}

/**
 * Response with available addons for the requested slot
 */
export interface AddonAvailabilityResponse {
  addons: AddonItem[];
  durationMinutes: number;
  durationHours: number;
}

/**
 * Pricing breakdown for a selected addon
 */
export interface AddonPricingSummary {
  addonItemId: string;
  addonName: string;
  quantity: number;
  pricingModel: AddonPricingModel;
  pricePerUnit: number;
  durationMultiplier: number; // 1 for fixed, hours for hourly
  subtotal: number;
}

/**
 * Vendor-specific addon relationship (complements global InventoryItemAddon)
 * Allows vendors to add custom addons beyond what Admin configured globally
 */
export interface VenueInventoryAddon {
  id: string;
  venueInventoryId: string;
  addonItemId: string;
  pricePerHour?: number;
  pricePerHalfHour?: number;
  maxQuantity?: number;
  isAvailable: boolean;
  displayOrder: number;
  source: 'GLOBAL' | 'VENDOR';
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Merged view combining global and vendor-specific addon configurations
 * Used to present unified addon list with effective pricing
 */
export interface MergedAddonConfig {
  addonItemId: string;
  addonName: string;
  source: 'GLOBAL' | 'VENDOR';
  pricingModel: AddonPricingModel | null;
  effectivePrice: {
    perHour?: number;
    perHalfHour?: number;
  };
  isAvailable: boolean;
  maxQuantity?: number;
  displayOrder: number;
}

/**
 * Zod schema for creating vendor-specific addon
 */
export const CreateVenueAddonSchema = z.object({
  addonItemId: z.string().uuid(),
  pricePerHour: z.number().positive().optional(),
  pricePerHalfHour: z.number().positive().optional(),
  maxQuantity: z.number().int().positive().optional(),
  isAvailable: z.boolean().default(true),
  displayOrder: z.number().int().min(0).default(999),
});

/**
 * Request DTO for creating vendor-specific addon
 */
export type CreateVenueAddonRequest = z.infer<typeof CreateVenueAddonSchema>;

/**
 * Zod schema for updating vendor-specific addon
 */
export const UpdateVenueAddonSchema = z.object({
  pricePerHour: z.number().positive().optional(),
  pricePerHalfHour: z.number().positive().optional(),
  maxQuantity: z.number().int().positive().optional(),
  isAvailable: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
});

/**
 * Request DTO for updating vendor-specific addon
 */
export type UpdateVenueAddonRequest = z.infer<typeof UpdateVenueAddonSchema>;

const AddonPricingOverrideSchema = z.object({
  pricePerHour: z.number().nullable(),
  pricePerHalfHour: z.number().nullable(),
  maxQuantity: z.number().nullable(),
  isAvailable: z.boolean(),
});

// AddonConfigDto — fetched when vendor opens the edit pricing dialog
export const AddonConfigDtoSchema = z.object({
  addonItemId: z.string().uuid(),
  venueInventoryId: z.string().uuid(),
  addonName: z.string(),
  source: z.enum(['GLOBAL', 'VENDOR']),
  pricingModel: z.nativeEnum(AddonPricingModel).nullable(),
  globalDefaults: z
    .object({
      pricePerHour: z.number().nullable(),
      pricePerHalfHour: z.number().nullable(),
      maxAdditionalQuantity: z.number().nullable(),
      includedQuantity: z.number(),
      isRequired: z.boolean(),
    })
    .nullable(),
  venueOverrides: AddonPricingOverrideSchema.nullable(),
  vendorConfig: AddonPricingOverrideSchema.nullable(),
});
export type AddonConfigDto = z.infer<typeof AddonConfigDtoSchema>;

// --- Bundle Types ---
export const CreateBundleSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  bundlePrice: z.number().positive(),
  displayOrder: z.number().int().min(0).default(0),
  items: z
    .array(
      z.object({
        venueInventoryId: z.string().uuid(),
        quantity: z.number().int().min(1).max(100),
      })
    )
    .min(2, 'Bundle must have at least 2 items'),
});
export type CreateBundleDto = z.infer<typeof CreateBundleSchema>;

export const UpdateBundleSchema = CreateBundleSchema.partial();
export type UpdateBundleDto = z.infer<typeof UpdateBundleSchema>;

export const BundleItemDtoSchema = z.object({
  venueInventoryId: z.string().uuid(),
  itemName: z.string(),
  quantity: z.number(),
  individualPrice: z.number(),
});

export const BundleDtoSchema = z.object({
  id: z.string().uuid(),
  venueId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  bundlePrice: z.number(),
  individualTotal: z.number(),
  savings: z.number(),
  savingsPercent: z.number(),
  isActive: z.boolean(),
  displayOrder: z.number(),
  items: z.array(BundleItemDtoSchema),
});
export type BundleDto = z.infer<typeof BundleDtoSchema>;

// ============================================================
// Peak Pricing Schemas (Task 28)
// ============================================================

export const PricingScheduleTypeSchema = z.enum(['PEAK', 'OFF_PEAK', 'SPECIAL']);
export type PricingScheduleType = z.infer<typeof PricingScheduleTypeSchema>;

export const CreatePricingScheduleSchema = z.object({
  venueInventoryId: z.string().uuid(),
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/), // HH:MM
  endTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
  priceMultiplier: z.number().min(0.1).max(10),
  overridePrice: z.number().positive().optional(), // absolute price per hour (replaces multiplier when set)
  tierName: z.string().trim().max(50).optional(), // label like "Happy Hour", "Late Night"
  scheduleType: PricingScheduleTypeSchema,
  specificDate: z.string().optional(),
});
export type CreatePricingSchedule = z.infer<typeof CreatePricingScheduleSchema>;

export const PricingScheduleDtoSchema = CreatePricingScheduleSchema.extend({
  id: z.string().uuid(),
  createdAt: z.string(),
});
export type PricingScheduleDto = z.infer<typeof PricingScheduleDtoSchema>;

export const BulkUpsertPricingScheduleSchema = z.object({
  schedules: z
    .array(CreatePricingScheduleSchema.omit({ venueInventoryId: true }))
    .min(1)
    .max(48),
});
export type BulkUpsertPricingSchedule = z.infer<typeof BulkUpsertPricingScheduleSchema>;
